<template>
  <v-container fluid class="pa-0 bg-slate-50 min-h-screen" style="font-family: 'Inter', sans-serif;">
    <div class="p-4 md:p-8">
      <div class="max-w-4xl mx-auto">
        
        <!-- HEADER -->
        <div class="mb-8 flex items-center justify-between">
          <div>
            <button @click="$router.back()" class="flex items-center gap-2 text-slate-400 hover:text-orange-500 transition-all mb-2 group">
              <v-icon small color="currentColor" class="transition-transform group-hover:-translate-x-1">mdi-arrow-left</v-icon>
              <span class="text-[10px] font-black uppercase tracking-[0.2em]">Voltar</span>
            </button>
            <h1 class="text-2xl font-black text-slate-800 tracking-tight">{{ id ? 'Editar Nota Fiscal' : 'Lançar Nota Fiscal' }}</h1>
            <p class="text-sm text-slate-500 font-medium">Preencha as informações para {{ id ? 'atualizar' : 'cadastrar' }} a nota fiscal no sistema</p>
          </div>
        </div>

        <v-form ref="form" v-model="valid" lazy-validation class="space-y-8 pb-12">
          
          <!-- SEÇÃO 1: IDENTIFICAÇÃO -->
          <div class="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
            <div class="px-6 py-4 flex items-center gap-3" style="background-color: #333333;">
              <v-icon color="orange" small>mdi-file-document-outline</v-icon>
              <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">IDENTIFICAÇÃO DA NOTA</h2>
            </div>
            
            <div class="p-8 space-y-6">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label class="text-[10px] font-black text-slate-400 uppercase tracking-[0.15em] ml-1 mb-2 block">Prefeitura</label>
                  <v-autocomplete
                    :items="prefectures"
                    item-value="prefecture_id"
                    item-text="prefecture_social_name"
                    v-model="data.prefecture_id"
                    @change="[loadProviders(), loadSec(), getVehiclesByPrefecture()]"
                    :readonly="formDisable"
                    placeholder="SELECIONE A PREFEITURA..."
                    solo flat dense hide-details
                    class="custom-select"
                    height="48"
                  />
                </div>
                <div>
                  <label class="text-[10px] font-black text-slate-400 uppercase tracking-[0.15em] ml-1 mb-2 block">Participante</label>
                  <v-autocomplete
                    :items="providers"
                    item-value="provider_id"
                    item-text="provider_name"
                    v-model="data.provider_id"
                    @change="[loadProvidersItem()]"
                    placeholder="SELECIONE O PARTICIPANTE..."
                    solo flat dense hide-details
                    class="custom-select"
                    height="48"
                  />
                </div>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label class="text-[10px] font-black text-slate-400 uppercase tracking-[0.15em] ml-1 mb-2 block">Veículo</label>
                  <v-autocomplete
                    v-model="data.vehicle_id"
                    :items="vehicles"
                    item-value="vehicle_id"
                    placeholder="SELECIONE O VEÍCULO..."
                    solo flat dense hide-details
                    class="custom-select"
                    height="48"
                  >
                    <template slot="selection" slot-scope="{ item }">
                      {{ item.brand_desc }} - {{ item.model_desc }} - {{ item.vehicle_board }}
                    </template>
                    <template slot="item" slot-scope="{ item }">
                      {{ item.brand_desc }} - {{ item.model_desc }} - {{ item.vehicle_board }}
                    </template>
                  </v-autocomplete>
                </div>
                <div class="grid grid-cols-2 gap-4">
                  <div>
                    <label class="text-[10px] font-black text-slate-400 uppercase tracking-[0.15em] ml-1 mb-2 block">Número NF</label>
                    <input 
                      type="text" 
                      v-model="data.nf_number"
                      v-mask-number
                      placeholder="000"
                      class="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-700 focus:outline-none focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 transition-all font-semibold"
                    >
                  </div>
                  <div>
                    <label class="text-[10px] font-black text-slate-400 uppercase tracking-[0.15em] ml-1 mb-2 block">Data Emissão</label>
                    <input 
                      type="text" 
                      v-model="data.nf_issue_date"
                      v-mask-date.br
                      placeholder="DD/MM/AAAA"
                      class="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-700 focus:outline-none focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 transition-all font-semibold"
                    >
                  </div>
                </div>
              </div>

              <div>
                <label class="text-[10px] font-black text-slate-400 uppercase tracking-[0.15em] ml-1 mb-2 block">Nome Completo</label>
                <input 
                  type="text" 
                  v-model="data.nf_name"
                  placeholder="NOME DO RESPONSÁVEL"
                  class="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-700 focus:outline-none focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 transition-all uppercase font-semibold"
                >
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label class="text-[10px] font-black text-slate-400 uppercase tracking-[0.15em] ml-1 mb-2 block">Unidade Orçamentária</label>
                  <v-autocomplete
                    :items="prefectures_sec"
                    item-value="sec_id"
                    item-text="sec_name"
                    v-model="data.nf_sec_id"
                    placeholder="SELECIONE A UNIDADE..."
                    solo flat dense hide-details
                    class="custom-select"
                    height="48"
                    append-icon="mdi-plus"
                    @click:append="bidding_id ? $router.push(`/parts-and-services/biddings-manager/edit/${bidding_id}?tab=1`) : null"
                  />
                </div>
                <div>
                  <label class="text-[10px] font-black text-slate-400 uppercase tracking-[0.15em] ml-1 mb-2 block">U.O. Destinatário</label>
                  <v-autocomplete
                    :items="prefectures_sec"
                    item-value="sec_id"
                    item-text="sec_name"
                    v-model="data.nf_sec_recipient_id"
                    placeholder="SELECIONE A UNIDADE..."
                    solo flat dense hide-details
                    class="custom-select"
                    height="48"
                    append-icon="mdi-plus"
                    @click:append="bidding_id ? $router.push(`/parts-and-services/biddings-manager/edit/${bidding_id}?tab=1`) : null"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- SEÇÃO 2: ITENS E SERVIÇOS -->
          <div class="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
            <div class="px-6 py-4 flex items-center justify-between" style="background-color: #333333;">
              <div class="flex items-center gap-3">
                <v-icon color="orange" small>mdi-format-list-checks</v-icon>
                <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">ITENS E SERVIÇOS</h2>
              </div>
              <div class="flex gap-2">
                <button 
                  type="button" 
                  @click="addFieldParts"
                  class="bg-white/10 hover:bg-white/20 text-white rounded-lg px-4 py-2 transition-all font-bold text-[9px] uppercase tracking-widest flex items-center gap-2"
                >
                  <v-icon x-small color="white">mdi-plus</v-icon>
                  ADICIONAR PEÇA
                </button>
                <button 
                  type="button" 
                  @click="addFieldServices"
                  class="bg-white/10 hover:bg-white/20 text-white rounded-lg px-4 py-2 transition-all font-bold text-[9px] uppercase tracking-widest flex items-center gap-2"
                >
                  <v-icon x-small color="white">mdi-plus</v-icon>
                  ADICIONAR SERVIÇO
                </button>
              </div>
            </div>
            
            <div class="p-8">
              <div v-if="itens.length === 0" class="py-12 border-2 border-dashed border-slate-100 rounded-2xl flex flex-col items-center justify-center text-slate-300">
                <v-icon size="40" color="slate-100">mdi-playlist-plus</v-icon>
                <p class="mt-2 text-[10px] font-black uppercase tracking-widest text-slate-400">Nenhum item adicionado</p>
              </div>

              <div class="space-y-4">
                <div v-for="(item, i) in itens" :key="i" class="p-4 bg-slate-50/50 rounded-2xl border border-slate-100 transition-all hover:bg-slate-50 relative group">
                  <v-row align="center" no-gutters v-if="item.isServico">
                    <v-col cols="12" md="8" class="pr-4">
                      <label class="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 block">Serviço</label>
                      <input 
                        type="text" 
                        v-model="item.servico"
                        placeholder="DESCRIÇÃO DO SERVIÇO"
                        class="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:border-orange-500 transition-all font-semibold uppercase"
                      >
                    </v-col>
                    <v-col cols="12" md="3" class="pr-4 mt-4 md:mt-0">
                      <label class="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 block">Preço</label>
                      <div class="relative">
                        <span class="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs font-bold leading-none">R$</span>
                        <input 
                          type="text" 
                          v-model="item.preco"
                          v-mask-decimal.br="2"
                          @change="onChangeTotal()"
                          class="w-full bg-white border border-slate-200 rounded-lg pl-9 pr-3 py-2 text-sm text-slate-700 focus:outline-none focus:border-orange-500 transition-all font-semibold"
                        >
                      </div>
                    </v-col>
                    <v-col cols="12" md="1" class="mt-4 md:mt-0 flex justify-end items-end h-full pt-4 md:pt-5">
                      <button 
                        type="button" 
                        @click="removeField(i)"
                        class="p-2 text-rose-300 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-all"
                      >
                        <v-icon small color="currentColor">mdi-trash-can-outline</v-icon>
                      </button>
                    </v-col>
                  </v-row>

                  <v-row align="center" no-gutters v-else>
                    <v-col cols="12" md="4" class="pr-4">
                      <label class="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 block">Peça</label>
                      <v-autocomplete
                        :items="providersItem"
                        item-value="item_id"
                        v-model="item.peca_id"
                        placeholder="BUSCAR PEÇA..."
                        solo flat dense hide-details
                        class="custom-select-small"
                        height="38"
                      >
                        <template slot="item" slot-scope="{ item }">
                          <span class="text-xs">{{ `${item.name} - ${item.marca} - ${item.modelo}` }}</span>
                        </template>
                        <template slot="selection" slot-scope="{ item }">
                          <span class="text-xs">{{ `${item.name} - ${item.marca}` }}</span>
                        </template>
                      </v-autocomplete>
                    </v-col>
                    <v-col cols="12" md="3" class="pr-4 mt-4 md:mt-0">
                      <label class="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 block">Preço Unitário</label>
                      <div class="relative">
                        <span class="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs font-bold leading-none">R$</span>
                        <input 
                          type="text" 
                          v-model="item.preco"
                          v-mask-decimal.br="2"
                          @change="onChangeParts(i)"
                          class="w-full bg-white border border-slate-200 rounded-lg pl-9 pr-3 py-2 text-sm text-slate-700 focus:outline-none focus:border-orange-500 transition-all font-semibold"
                        >
                      </div>
                    </v-col>
                    <v-col cols="12" md="2" class="pr-4 mt-4 md:mt-0">
                      <label class="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 block">Qtd</label>
                      <input 
                        type="text" 
                        v-model="item.quantidade"
                        v-mask-number
                        @change="[onChangeParts(i), onChangeTotal()]"
                        placeholder="0"
                        class="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:border-orange-500 transition-all font-semibold text-center"
                      >
                    </v-col>
                    <v-col cols="12" md="2" class="pr-4 mt-4 md:mt-0">
                      <label class="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 block">Total Item</label>
                      <div class="relative">
                        <span class="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs font-bold leading-none">R$</span>
                        <input 
                          type="text" 
                          v-model="item.total"
                          v-mask-decimal.br="2"
                          readonly
                          class="w-full bg-slate-100 border border-slate-100 rounded-lg pl-9 pr-3 py-2 text-sm text-slate-500 focus:outline-none font-bold"
                        >
                      </div>
                    </v-col>
                    <v-col cols="12" md="1" class="mt-4 md:mt-0 flex justify-end items-end h-full pt-4 md:pt-5">
                      <button 
                        type="button" 
                        @click="removeField(i)"
                        class="p-2 text-rose-300 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-all"
                      >
                        <v-icon small color="currentColor">mdi-trash-can-outline</v-icon>
                      </button>
                    </v-col>
                  </v-row>
                </div>
              </div>
            </div>
          </div>

          <!-- SEÇÃO 3: TOTAIS -->
          <div class="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
            <div class="px-6 py-4 flex items-center gap-3" style="background-color: #333333;">
              <v-icon color="orange" small>mdi-cash-register</v-icon>
              <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">RESUMO FINANCEIRO</h2>
            </div>
            
            <div class="p-8">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div class="p-6 bg-slate-50 rounded-2xl border border-slate-100">
                  <label class="text-[10px] font-black text-slate-400 uppercase tracking-[0.15em] ml-1 mb-2 block">Desconto Aplicado</label>
                  <div class="relative">
                    <span class="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-bold">R$</span>
                    <input 
                      type="text" 
                      v-model="data.nf_discount"
                      v-mask-decimal.br="2"
                      @change="onChangeTotal()"
                      placeholder="0,00"
                      class="w-full bg-white border border-slate-200 rounded-xl pl-12 pr-4 py-4 text-xl text-rose-500 focus:outline-none focus:border-rose-500 focus:ring-4 focus:ring-rose-500/10 transition-all font-black"
                    >
                  </div>
                </div>

                <div class="p-6 bg-orange-50 rounded-2xl border border-orange-100">
                  <label class="text-[10px] font-black text-orange-400 uppercase tracking-[0.15em] ml-1 mb-2 block">Valor Total da Nota</label>
                  <div class="relative">
                    <span class="absolute left-4 top-1/2 -translate-y-1/2 text-orange-400 font-bold">R$</span>
                    <input 
                      type="text" 
                      v-model="data.nf_total"
                      v-mask-decimal.br="2"
                      readonly
                      placeholder="0,00"
                      class="w-full bg-white border border-orange-200 rounded-xl pl-12 pr-4 py-4 text-2xl text-orange-600 focus:outline-none transition-all font-black"
                    >
                  </div>
                </div>
              </div>

              <!-- FOOTER (PADRÃO VEÍCULOS) -->
              <div class="mt-12 bg-slate-50 px-8 py-5 border-t border-slate-200 flex justify-end items-center gap-4 -mx-8 -mb-8">
                <button 
                  type="button"
                  @click="$router.back()"
                  class="text-slate-500 hover:text-slate-700 font-bold text-[11px] uppercase px-4 py-2 transition-colors flex-none"
                >
                  Descartar
                </button>
                <div class="flex-none">
                  <button
                    type="button"
                    @click="validate"
                    :disabled="loader"
                    class="bg-orange-500 hover:bg-orange-600 text-white font-bold text-[11px] px-6 py-2.5 rounded shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all w-auto max-w-max whitespace-nowrap flex items-center gap-2"
                  >
                    <v-icon small color="white" v-if="!loader" style="font-size: 14px;">mdi-check</v-icon>
                    <span v-if="loader" class="animate-spin h-3 w-3 border-2 border-white/30 border-t-white rounded-full"></span>
                    <span>{{ id ? 'SALVAR ALTERAÇÕES' : 'CONFIRMAR LANÇAMENTO' }}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </v-form>

        <!-- NOTIFICAÇÕES -->
        <v-snackbar
          v-model="snack.show"
          :color="snack.color"
          timeout="4000"
          top
          elevation="24"
          class="custom-snack"
        >
          <div class="flex items-center gap-3">
            <v-icon color="white" small>{{ snack.color === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
            <span class="font-bold text-xs uppercase tracking-wider">{{ snack.message }}</span>
          </div>
          <template v-slot:action="{ attrs }">
            <v-btn dark text v-bind="attrs" @click="snack.show = false" class="bg-white/10 ml-4 rounded">
              <v-icon size="16">mdi-close</v-icon>
            </v-btn>
          </template>
        </v-snackbar>
      </div>
    </div>
  </v-container>
</template>

<script>
export default {
  props: ["id"],
  data: () => ({
    valid: false,
    loader: false,
    fieldRules: [(v) => !!v || "Este campo é requerido"],
    snack: {
      show: false,
      message: "",
      color: "success",
    },
    data: {
      nf_id: null,
      nf_number: "",
      prefecture_id: null,
      provider_id: null,
      vehicle_id: null,
      nf_issue_date: "",
      nf_name: "",
      nf_sec_id: null,
      nf_sec_recipient_id: null,
      nf_item: null,
      nf_discount: "",
      nf_total: "",
    },
    prefectures: [],
    providers: [],
    providersItem: [],
    itens: [],
    vehicles: [],
    formDisable: false,
    prefectures_sec: [],
    bidding_id: null,
  }),
  created() {
    this.$http.get("/prefectures").then((r) => (this.prefectures = r.data));
    if (this.id) {
      this.$store.dispatch("setLoader", true);
      this.$http.get(`/parts-and-services/nf/${this.id}`).then((r) => {
        this.data = r.data[0];
        delete this.data.created_at;
        this.loadProviders();
        this.loadSec();
        this.loadProvidersItem();
        this.getVehiclesByPrefecture();
        if (this.data.nf_issue_date) {
          this.data.nf_issue_date = this.formatDateBr(this.data.nf_issue_date);
        }
        this.itens = JSON.parse(this.data.nf_item);
        this.$store.dispatch("setLoader", false);
      });
    }
    if (this.$store.getters.getUserData.table_key == 'prefectures') {
      this.formDisable = true;
      this.data.prefecture_id = this.$store.getters.getUserData.table_id;
      this.loadProviders();
      this.loadSec();
    }
  },
  methods: {
    onChangeParts(i) {
      if (this.itens[i].preco && this.itens[i].quantidade) {
        const value = parseFloat(this.itens[i].preco.split(".").join("").replace(",", ".")) * parseInt(this.itens[i].quantidade, 10);
        this.itens[i].total = value.toFixed(2).toString().replace('.', ',');
      }
    },
    onChangeTotal() {
      let total = 0;
      this.itens.map((item) => {
        if (item.isServico && item.preco) {
          total += parseFloat(item.preco.split(".").join("").replace(",", "."));
        } else if (item.total) {
          total += parseFloat(item.total.split(".").join("").replace(",", "."));
        }
      });
      if (this.data.nf_discount) {
        total -= parseFloat(this.data.nf_discount.split(".").join("").replace(",", "."));
      }
      this.data.nf_total = total.toFixed(2).toString().replace('.', ',');
    },
    getVehiclesByPrefecture() {
      this.loader = true;
      this.$store.dispatch("setLoader", this.loader);
      this.$http
        .get(`/vehicles/prefectures/${this.data.prefecture_id}`)
        .then((v) => {
          this.vehicles = v.data;
          this.loader = false;
          this.$store.dispatch("setLoader", this.loader);
        })
        .catch((e) => {
          this.loader = false;
          this.snack.show = true;
          this.snack.message = e;
          this.snack.color = "error";
          this.$store.dispatch("setLoader", this.loader);
        });
    },
    addFieldParts() {
      this.itens.push({
        peca_id: null,
        preco: "",
        quantidade: "",
        total: "",
        isServico: false,
      });
    },
    addFieldServices() {
      this.itens.push({
        servico: "",
        preco: "",
        isServico: true,
      });
    },
    removeField(index) {
      this.itens.splice(index, 1);
      this.onChangeTotal();
    },
    formatDateBr(date) {
      return date.substr(0, 10).split("-").reverse().join("/");
    },
    validate() {
      if (this.$refs.form.validate()) {
        this.createOrUpdate();
      } else {
        this.snack.show = true;
        this.snack.color = "warning";
        this.snack.message = "Atencão: um ou mais campos precisam ser preenchidos!";
      }
    },
    createOrUpdate() {
      this.loader = true;
      this.data.nf_item = JSON.stringify(this.itens);
      
      const prepareValue = (val) => {
        if (!val || val === "") return "0.00";
        return val.toString().split(".").join("").replace(",", ".");
      };

      this.data.nf_discount = prepareValue(this.data.nf_discount);
      this.data.nf_total = prepareValue(this.data.nf_total);

      this.$store.dispatch("setLoader", true);

      const request = this.id 
        ? this.$http.put(`/parts-and-services/nf/${this.id}`, this.data)
        : (this.data.itens = this.itens, this.$http.post("/parts-and-services/nf", this.data));

      request
        .then((r) => {
          this.snack.show = true;
          if (r.data.result || r.data.success) {
            this.snack.message = r.data.result || r.data.success;
            this.snack.color = "success";
            setTimeout(() => {
              this.$router.push("/parts-and-services/nf");
            }, 1500);
          } else {
            this.snack.message = r.data.error;
            this.snack.color = "error";
            this.loader = false;
            this.$store.dispatch("setLoader", false);
          }
        })
        .catch((e) => {
          this.snack.show = true;
          this.snack.message = e.message;
          this.snack.color = "error";
          this.loader = false;
          this.$store.dispatch("setLoader", false);
        });
    },
    loadProviders() {
      this.$http
        .get(`/parts-and-services/providers/prefecture/${this.data.prefecture_id}`)
        .then((r) => (this.providers = r.data));
    },
    loadProvidersItem() {
      this.$http
        .get(`/parts-and-services/nf/providers/${this.data.provider_id}`)
        .then((r) => {
          if (r.data.length > 0) {
            this.providersItem = r.data;
            this.bidding_id = r.data[0].bidding_id;
          }
        });
    },
    loadSec() {
      this.$http
        .get(`/prefectures/secretariats/${this.data.prefecture_id}`)
        .then((r) => (this.prefectures_sec = r.data));
    },
  },
};
</script>

<style scoped>
.custom-select ::v-deep .v-input__slot {
  background: #f8fafc !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: none !important;
  border-radius: 12px !important;
  height: 48px !important;
  padding: 0 16px !important;
}

.custom-select-small ::v-deep .v-input__slot {
  background: white !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: none !important;
  border-radius: 8px !important;
  height: 38px !important;
}

.custom-select ::v-deep input, .custom-select-small ::v-deep input {
  font-size: 14px !important;
  font-weight: 600 !important;
  color: #334155 !important;
  text-transform: uppercase !important;
}

.custom-select-small ::v-deep input {
  font-size: 12px !important;
}

.custom-select ::v-deep .v-label {
  font-size: 10px !important;
  font-weight: 900 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.15em !important;
  color: #94a3b8 !important;
}

/* Animations */
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
.animate-spin {
  animation: spin 1s linear infinite;
}
</style>
